import Cookies from "js-cookie";
import { getSession, signIn, signOut } from "next-auth/react";
import { useRouter } from "next/router";
import queryString from "query-string";
import { useCallback, useState } from "react";
import { useRecoilState, useSetRecoilState } from "recoil";

import {
  DoctorModel,
  EvaluationValidation,
  GetCartDocument,
  GetDoctorInAuthDocument,
  GetDoctorInAuthQuery,
  GetPatientInfoDocument,
  ImpersonateDocument,
  ImpersonateMutation,
  PatientModel,
  PlatformType,
  RegionType,
  RegisterPatientMutationVariables,
  SendResetPasswordEmailDocument,
  SendResetPasswordEmailMutation,
  SendResetPasswordEmailMutationVariables,
  UpdatePatientInfoDocument,
  UpdatePatientInfoMutation,
  UpdatePatientInfoMutationVariables,
  UserRole,
  useRegisterPatientMutation
} from "graphql/generated";
import graphQLClient from "graphql/react-query";
import { trackingUserId } from "src/constants/cookie-names.constant";
import { TRACKING_EVENTS_2 } from "src/dataTracking/events";
import { track$ } from "src/dataTracking/segment";
import { showError, uuidGen } from "src/utils";
import { isDoctorRole, isPatientRole } from "src/utils/roles";

import { config } from "../config";
import { CURRENT_COUNTRY, CURRENT_DEFAULT_LANG } from "../constants/Countries";
import { CHECKOUT_ERROR, evaluationLSName } from "../constants/local-storage-names.constant";
import { AuthState, authState, doctorState, patientState, platformState } from "../states";
import { AuthUserInfo } from "../types/auth";
import { getTracking, getUtm } from "../utils/tracking";
import { getAnonymousId } from "./useTrackPageEvent";

const PARAM_ORDER = ["redirect"];

export type RegisterParams = {
  email: string;
  password: string;
  locale: string;
  firstName?: string;
  lastName?: string;
  country: RegionType;
  dob?: string;
  anonymousPatientId?: string; // only apply for prenatal register
  fromPlatform?: PlatformType;
};

export type LoginParams = {
  email: string;
  password: string;
  unredirect?: boolean;
  impersonate?: boolean;
  cb?: () => void;
};

export const IMPERSONATE_DOCTOR_ERROR = "impersonate-doctor";

export interface UseAuth {
  onLogin: (params: LoginParams) => Promise<string>;
  onLogout: () => Promise<void>;
  onRegister: (params: RegisterParams) => Promise<void>;
  hasPermission: () => Promise<boolean>;
  checkTokenInfo: () => Promise<void>;
  getPatient: () => Promise<PatientModel | null | undefined>;
  onSendResetPasswordEmail: (values: any) => Promise<void>;
  onGoogleSSOLogin: () => Promise<string>;
  onFacebookSSOLogin: () => Promise<string>;
  onYahooSSOLogin: () => Promise<string>;
  onLineSSOLogin: () => Promise<string>;
  createPatientForLoginUser: (platform: PlatformType) => Promise<void>;
  isLoading: boolean;
}

const sortParams = (a: string, b: string): number =>
  (PARAM_ORDER.indexOf(a) === -1 ? 1 : 0) - (PARAM_ORDER.indexOf(b) === -1 ? 1 : 0);

export const useAuth = (): UseAuth => {
  const setAuth = useSetRecoilState(authState);
  const [patient, setPatient] = useRecoilState(patientState);
  const setPlatform = useSetRecoilState(platformState);
  const setDoctor = useSetRecoilState(doctorState);
  const [isLoading, setIsLoading] = useState(false);
  const { mutateAsync: registerPatientMutation } = useRegisterPatientMutation(graphQLClient);
  const router = useRouter();

  const getCartItems = async () => {
    const res = await graphQLClient.request(GetCartDocument, {
      input: {}
    });

    if (res?.getCart.cartItems) {
      return res?.getCart.cartItems as object[];
    }

    return [];
  };

  const updatePatientDOB = async (dob: string) => {
    await graphQLClient.request<UpdatePatientInfoMutation, UpdatePatientInfoMutationVariables>(
      UpdatePatientInfoDocument,
      {
        input: {
          dob: new Date(dob)
        }
      }
    );
  };

  const getDOBFromLocalStorage = (evalName: string): string => {
    let localEval: any = null;
    try {
      localEval = JSON.parse(localStorage.getItem(evaluationLSName(evalName)));
    } catch (e) {
      console.log("Cannot parse JSON: ", e);
      return "";
    }

    const dobQA: any = Object.values(localEval)
      .filter((item) => Array.isArray(item))
      .flat()
      .find((qa: any) => {
        return qa?.question?.validation?.type === "DOB";
      });

    return dobQA?.value || "";
  };

  const extractPtDetailFromLocalStorageEvalAnswers = (evalName: string): PatientModel => {
    const patientDetail = {
      dob: null,
      height: 0,
      weight: 0,
      bloodPressure: null
    };

    let localEval: any = null;

    try {
      localEval = JSON.parse(localStorage.getItem(evaluationLSName(evalName)));
    } catch (e) {
      console.error("Cannot parse JSON: ", e);
      return null;
    }

    Object.values(localEval)
      .filter((item) => Array.isArray(item))
      .flat()
      .forEach((qa: any) => {
        // not have type because validation is forced to be inserted in

        switch (qa.question?.validation?.type) {
          case EvaluationValidation.Dob:
            patientDetail.dob = qa.value;
            break;

          case EvaluationValidation.Height:
            patientDetail.height = qa.value;
            break;

          case EvaluationValidation.Weight:
            patientDetail.weight = qa.value;
            break;

          case EvaluationValidation.BloodPressure:
            patientDetail.bloodPressure = qa.value;
            break;

          default:
            break;
        }
      });

    return patientDetail;
  };

  const hasPermission = useCallback(
    async (p?: PatientModel) => {
      try {
        const data = patient || p;

        if (!data) {
          return false;
        }

        return true;
      } catch (error) {
        showError(error.message);
        console.error(error);

        throw new Error("Can't check permission");
      }
    },
    [patient]
  );

  const getPatientByUserId = useCallback(async (): Promise<PatientModel | null | undefined> => {
    try {
      const patientData = await graphQLClient.request(GetPatientInfoDocument, {});
      return patientData?.getPatientInfo;
    } catch (error) {
      console.error(error);
    }
  }, []);

  const getDoctorInfo = useCallback(async (): Promise<DoctorModel> => {
    try {
      const doctorRes = await graphQLClient.request<GetDoctorInAuthQuery>(
        GetDoctorInAuthDocument,
        {}
      );
      return doctorRes.getDoctorInAuth;
    } catch (error) {
      console.error(error);
    }
  }, []);

  const cartItemsIncludeCheckingEvalId = (
    platform: PlatformType,
    cartItems: any[],
    evalId: string
  ): boolean => {
    if (!evalId?.trim()) {
      return false;
    }

    return cartItems.some((item: any) =>
      [item.product.evaluationId[platform], item.teleConsultForSkipEval].includes(evalId)
    );
  };

  const userWithFFlip = (userData: AuthUserInfo, patientData: PatientModel) => {
    const user = {
      key: userData.sysId,
      firstName: userData.firstName,
      lastName: userData.lastName,
      email: userData.email,
      userType: userData.userType,
      fromPlatform: patientData.fromPlatformEnv,
      country: patientData.country
    };
    Cookies.set(config.COOKIES.IDENTIFY_USER, JSON.stringify(user));
  };

  const checkSameTreatmentInCart = async (
    platform: PlatformType,
    evalId: string,
    cartItems
  ): Promise<boolean> => {
    if (cartItemsIncludeCheckingEvalId(platform, cartItems, evalId)) {
      localStorage.setItem(CHECKOUT_ERROR, "1");
      await router.push("/checkout");
      return true;
    }

    return false;
  };

  /**
   * Get redirect url after login
   * @returns redirect url string
   */
  const getRedirectUrl = () => {
    const queryParamsValue = {
      ...router.query
    };

    const redirectPath = (queryParamsValue.redirect || "/") as string;
    delete queryParamsValue.type;
    delete queryParamsValue.redirect;

    const queryParamsStr = queryString.stringify(queryParamsValue, {
      skipNull: true,
      sort: sortParams,
      encode: false
    });

    if (queryParamsStr === "returnUrl=/" && redirectPath.startsWith("/booking?orderId=")) {
      // this handle specific case for review consult email from the cronjob handleCronSubRenewalEmailReminder in BE
      const redirectUrl = `${redirectPath}&${queryParamsStr}`;
      return redirectUrl;
    }
    if (queryParamsStr) {
      const redirectUrl = `${redirectPath}?${queryParamsStr}`;
      return redirectUrl;
    }
    return redirectPath;
  };
  /**
   *
   * @param {AuthState} user auth state after checking authentication
   * @param {string} email patient email
   * @param {boolean} unredirect prevent redirect after logging in
   * @returns {string} patient id
   */
  const handlePatientStuffAfterLoggingIn = async (
    user: AuthState,
    currentPatient: PatientModel,
    email: string, // lowerCaseEmail
    unredirect: boolean
  ) => {
    const hasPer = await hasPermission(currentPatient);
    if (!hasPer) {
      await onLogout(false);
      throw new Error("User doesn't have permission");
    }
    setPatient(currentPatient);
    setAuth(user);
    userWithFFlip(user, currentPatient);
    setPlatform(currentPatient.fromPlatformEnv);

    if (!unredirect) {
      const queryParamsValue = {
        ...router.query
      };

      const redirectPath = (queryParamsValue.redirect || "/") as string;

      delete queryParamsValue.type;
      delete queryParamsValue.redirect;

      const cartItems = await getCartItems();
      if (redirectPath.startsWith("/orderByCondition") && cartItems.length !== 0) {
        const evalIdFromUrl = redirectPath.slice(1).split("?")[0].split("/")[1];
        const res = await checkSameTreatmentInCart(
          currentPatient.fromPlatformEnv,
          evalIdFromUrl,
          cartItems
        );
        if (res) {
          return;
        }
      } else if (redirectPath.startsWith("/evaluation/completion")) {
        const evalName = redirectPath.split("?")[0].split("/").pop();
        const dob = getDOBFromLocalStorage(evalName);
        if (dob) {
          await updatePatientDOB(dob);
        }

        const res = await checkSameTreatmentInCart(
          currentPatient.fromPlatformEnv,
          evalName,
          cartItems
        );
        if (res) {
          return;
        }
      }

      const queryParamsStr = queryString.stringify(queryParamsValue, {
        skipNull: true,
        sort: sortParams,
        encode: false
      });

      if (queryParamsStr === "returnUrl=/" && redirectPath.startsWith("/booking?orderId=")) {
        // this handle specific case for review consult email from the cronjob handleCronSubRenewalEmailReminder in BE
        const redirectUrl = `${redirectPath}&${queryParamsStr}`;
        router.replace(redirectUrl);
      } else if (queryParamsStr) {
        const redirectUrl = `${redirectPath}?${queryParamsStr}`;
        router.replace(redirectUrl);
      } else {
        router.replace(redirectPath);
      }
    }
    // eslint-disable-next-line consistent-return
    return currentPatient.sysId;
  };

  const isValidDoctorPath = (redirectPath: string) => {
    return redirectPath.startsWith("/doctor");
  };

  const handleDoctorStuffAfterLoggingIn = async (user) => {
    setAuth(user);
    const queryParamsValue = {
      ...router.query
    };
    const redirectPath = (queryParamsValue.redirect || "/") as string;
    const d = await getDoctorInfo();
    setDoctor(d);
    if (isValidDoctorPath(redirectPath)) {
      await router.push(redirectPath);
      return;
    }

    await router.push("/doctor");
  };

  const isValidOpsPath = (redirectPath: string) => {
    return redirectPath.startsWith("/ops");
  };

  const handleOpsStuffAfterLoggingIn = async (user) => {
    setAuth(user);
    const queryParamsValue = {
      ...router.query
    };
    const redirectPath = (queryParamsValue.redirect || "/") as string;

    if (isValidOpsPath(redirectPath)) {
      await router.push(redirectPath);
    }
  };

  // return: patient id
  const onLogin = useCallback(
    async ({
      email,
      password,
      unredirect = false,
      impersonate = false,
      cb
    }: LoginParams): Promise<string> => {
      try {
        const lowerCaseEmail = email.toLowerCase();
        if (impersonate) {
          // The backend impersonate mutation is admin-guarded, so it must be
          // called from the already-authenticated graphQLClient here rather
          // than from NextAuth's authorize() (which runs without auth).
          const res = await graphQLClient.request<ImpersonateMutation>(ImpersonateDocument, {
            input: { email: lowerCaseEmail }
          });
          if (!res?.impersonate) {
            throw new Error("Impersonation failed");
          }
          // v1 /doctor pages redirect to v2, and AuthWrapper sends a doctor session there from every
          // route, so swapping in a doctor's session would leave the admin no way back to /ops.
          if (isDoctorRole(res.impersonate.user?.userType)) {
            throw new Error(IMPERSONATE_DOCTOR_ERROR);
          }
          await signIn("credentials", {
            isImpersonate: true,
            impersonationData: JSON.stringify(res.impersonate),
            redirect: false
          });
        } else {
          await signIn("credentials", {
            email: lowerCaseEmail,
            password,
            isImpersonate: false,
            redirect: false
          });
        }

        const session = await getSession();
        Cookies.set(config.COOKIES.ACCESS_TOKEN, session?.accessToken);
        Cookies.set(config.COOKIES.REFRESH_TOKEN, session?.refreshToken);
        graphQLClient.setHeader("Authorization", `Bearer ${session?.accessToken}`);

        const { user } = session || {};
        if (user?.id) {
          Cookies.set(trackingUserId, user?.id, { expires: 365 });
        }

        // in case impersonate false:
        if (impersonate && user?.userType !== UserRole.Patient) {
          cb();
        }

        let currentPatient;
        if (isPatientRole(user?.userType)) {
          currentPatient = await getPatientByUserId();
        }

        // Route doctor-likes (doctor + test_doctor) through a predicate guard so a
        // test_doctor does not fall through the switch's default and throw.
        if (isDoctorRole(user?.userType)) {
          await handleDoctorStuffAfterLoggingIn(user);
          return "";
        }

        // Route patient-likes (patient + test_patient) through a predicate guard so a
        // test_patient does not fall through the switch's default and throw. A test_patient
        // is a real patient (they place TRT orders); they log in to the consumer app as one,
        // and may additionally reach the ops TRT queue for their own test orders.
        if (isPatientRole(user?.userType)) {
          return await handlePatientStuffAfterLoggingIn(
            user,
            currentPatient,
            lowerCaseEmail,
            unredirect
          );
        }

        switch (user?.userType) {
          case UserRole.Admin:
            await handleOpsStuffAfterLoggingIn(user);
            return "";
          default:
            throw new Error(`Invalid user type: "${user?.userType}"`);
        }
      } catch (error) {
        console.error(error);

        throw error;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const onRegister = useCallback(
    async ({
      email,
      password,
      locale,
      country,
      anonymousPatientId,
      fromPlatform = PlatformType.Noah
    }: RegisterParams) => {
      const lowerCaseEmail = email.toLowerCase();
      let patientDetail: PatientModel = {
        dob: null,
        height: 0,
        weight: 0,
        bloodPressure: null
      };

      const queryParamsValue = {
        ...router.query
      };

      const redirectPath = (queryParamsValue.redirect || "/") as string;

      // guest flow
      if (redirectPath.startsWith("/evaluation/completion")) {
        const evalName = redirectPath.split("?")[0].split("/").pop();
        patientDetail = extractPtDetailFromLocalStorageEvalAnswers(evalName);
      }

      const payload: RegisterPatientMutationVariables = {
        input: {
          email: lowerCaseEmail,
          password,
          fromPlatformEnv: fromPlatform,
          dob: patientDetail.dob ? new Date(patientDetail.dob).toISOString() : null,
          height: patientDetail.height,
          weight: patientDetail.weight,
          bloodPressure: patientDetail.bloodPressure,
          country,
          locale: locale || CURRENT_DEFAULT_LANG[CURRENT_COUNTRY],
          fbc: localStorage.fbc,
          fbp: localStorage.fbp,
          utm: getUtm(),
          anonymousId: getAnonymousId(),
          tracking: getTracking(),
          anonymousPatientId
        }
      };

      try {
        await registerPatientMutation(payload);
        // no need to track identify here, because after registering, we will login right away
        // the login function includes tracking identify
      } catch (error) {
        console.error(error);
        throw new Error(error?.response?.errors?.[0]?.message);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const cleanupCookies = () => {
    Cookies.remove(config.COOKIES.NEXT_AUTH_SESSION_TOKEN, {
      path: "",
      domain: process.env.NEXT_PUBLIC_DOMAIN
    });
    Cookies.remove(config.COOKIES.NEXT_AUTH_SESSION_TOKEN, {
      path: "",
      domain: `.${process.env.NEXT_PUBLIC_DOMAIN}`
    });
    Cookies.remove(config.COOKIES.NEXT_AUTH_CALLBACK_URL);
    Cookies.remove(config.COOKIES.ACCESS_TOKEN);
    Cookies.remove(config.COOKIES.REFRESH_TOKEN);
    Cookies.remove(config.COOKIES.NEXT_LOCALE);
    Cookies.remove(trackingUserId);
  };

  const onLogout = useCallback(
    async (redirect = true) => {
      try {
        const platform = patient?.fromPlatformEnv;
        await track$({
          event: TRACKING_EVENTS_2.signedOut,
          anonymousId: getAnonymousId(),
          userId: patient?.user?.id,
          properties: {
            platform
          }
        });
        await signOut();
      } catch {
        console.log("Failed to signout");
      }

      graphQLClient.setHeader(config.COOKIES.ACCESS_TOKEN, "");
      localStorage.clear();
      cleanupCookies();

      if (redirect) {
        window.location.href = "/auth";
      }
    },
    [patient]
  );

  const onSendResetPasswordEmail = async (values) => {
    try {
      await graphQLClient.request<
        SendResetPasswordEmailMutation,
        SendResetPasswordEmailMutationVariables
      >(SendResetPasswordEmailDocument, {
        input: {
          email: values.email.toLowerCase(),
          url: window.location.origin,
          locale: values.locale
        }
      });
    } catch (error) {
      console.error(error);
      throw new Error(error.message);
    }
  };

  const checkTokenInfo = useCallback(async () => {
    const session = await getSession();

    const { user, accessToken, refreshToken } = session || {};
    if (!user?.userType || !accessToken) {
      return;
    }
    Cookies.set(config.COOKIES.ACCESS_TOKEN, accessToken);
    Cookies.set(config.COOKIES.REFRESH_TOKEN, refreshToken);

    graphQLClient.setHeader("Authorization", `Bearer ${accessToken}`);

    // Restore the doctor object for doctor-likes (doctor + test_doctor) via a
    // predicate guard so a refreshed test_doctor session keeps its doctor context.
    if (isDoctorRole(user.userType)) {
      const d = await getDoctorInfo();
      if (!d) {
        return;
      }

      setAuth(user);
      setDoctor(d);
      return;
    }

    // Restore the patient object for patient-likes (patient + test_patient) via a
    // predicate guard so a refreshed test_patient session keeps its patient context.
    if (isPatientRole(user.userType)) {
      const patientData = await getPatientByUserId();
      // guest user
      if (!patientData) {
        return;
      }

      setAuth(user);
      setPatient(patientData);
      setPlatform(patientData.fromPlatformEnv);
      const hasPer = await hasPermission(patientData);
      if (!hasPer) {
        console.log("checkTokenInfo");
        await onLogout(false);
        throw new Error("User doesn't have permission");
      }
      return;
    }

    switch (user.userType) {
      case UserRole.Admin: {
        setAuth(user);
        break;
      }
      default:
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setAuth, setPatient, setPlatform]);

  // return: patient id
  const onGoogleSSOLogin = useCallback(
    async (): Promise<string> => {
      try {
        const redirectUrl = getRedirectUrl();
        await signIn("google", {
          redirect: true,
          callbackUrl: redirectUrl
        });
        return "";
      } catch (error) {
        console.error(error);
        throw error;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // return: patient id
  const onFacebookSSOLogin = useCallback(
    async (): Promise<string> => {
      try {
        const redirectUrl = getRedirectUrl();
        await signIn("facebook", {
          redirect: true,
          callbackUrl: redirectUrl
        });
        return "";
      } catch (error) {
        console.error(error);
        throw error;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const onYahooSSOLogin = useCallback(
    async (): Promise<string> => {
      try {
        const redirectUrl = getRedirectUrl();
        await signIn("yahoo", {
          redirect: true,
          callbackUrl: redirectUrl
        });
        return "";
      } catch (error) {
        console.error(error);
        throw error;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const onLineSSOLogin = useCallback(
    async (): Promise<string> => {
      try {
        const redirectUrl = getRedirectUrl();
        await signIn("line", {
          redirect: true,
          callbackUrl: redirectUrl
        });
        return "";
      } catch (error) {
        console.error(error);
        throw error;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const createPatientForLoginUser = useCallback(
    async (platform: PlatformType, provider?: string) => {
      try {
        setIsLoading(true);
        const session = await getSession();
        const { user } = session || {};
        if (!user?.patient) {
          // new user, create patient
          const lowerCaseEmail = user?.email?.toLowerCase();
          let patientDetail: PatientModel = {
            dob: null,
            height: 0,
            weight: 0,
            bloodPressure: null
          };

          const redirectPath = (router.asPath || "/") as string;

          // guest flow
          if (redirectPath.startsWith("/evaluation/completion")) {
            const evalName = redirectPath.split("?")[0].split("/").pop();
            patientDetail = extractPtDetailFromLocalStorageEvalAnswers(evalName);
          }

          const password = uuidGen();

          const payload: RegisterPatientMutationVariables = {
            input: {
              email: lowerCaseEmail,
              firstName: user?.firstName,
              lastName: user?.lastName,
              password,
              fromPlatformEnv: platform,
              height: patientDetail.height,
              weight: patientDetail.weight,
              country: CURRENT_COUNTRY as RegionType,
              locale: CURRENT_DEFAULT_LANG[CURRENT_COUNTRY],
              fbc: localStorage.fbc,
              fbp: localStorage.fbp,
              utm: getUtm(),
              tracking: getTracking(),
              user,
              provider: provider || "email",
              dob: patientDetail.dob ? new Date(patientDetail.dob).toISOString() : null,
              bloodPressure: patientDetail.bloodPressure
            }
          };

          try {
            await registerPatientMutation(payload);

            await onLogin({
              email: lowerCaseEmail,
              password,
              unredirect: true
            });
          } catch (error) {
            console.error(error);
            throw new Error(error?.response?.errors?.[0]?.message);
          }
        }
      } catch (error) {
        console.error(error);
        throw new Error(error?.response?.errors?.[0]?.message);
      } finally {
        setIsLoading(false);
      }
    },
    [onLogin, registerPatientMutation, router.asPath]
  );

  return {
    onLogin,
    onLogout,
    onRegister,
    hasPermission,
    getPatient: getPatientByUserId,
    checkTokenInfo,
    onSendResetPasswordEmail,
    onGoogleSSOLogin,
    onFacebookSSOLogin,
    onYahooSSOLogin,
    onLineSSOLogin,
    createPatientForLoginUser,
    isLoading
  };
};
